import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  FirstConsultation,
  OutcomeOption,
} from '@/api/firstConsultations/firstConsultationsApi.types';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { useDownloadFirstConsultationPdf } from '@/hooks/useDownloadFirstConsultationPdf';
import {
  useShareFirstConsultationMutation,
  useUnshareFirstConsultationMutation,
} from '@/hooks/useFirstConsultationSharing';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { ConsultationVersionsList } from './ConsultationVersionsList';
import { OwnerPreviewModal } from './OwnerPreviewModal';
import { buildOwnerPreviewContent } from './ownerPreview';

interface FirstConsultationSharingCardProps {
  consultation: FirstConsultation;
  outcomeOptions: OutcomeOption[];
  hasUnsavedChanges: boolean;
  isEditorBusy: boolean;
}

export function FirstConsultationSharingCard({
  consultation,
  outcomeOptions,
  hasUnsavedChanges,
  isEditorBusy,
}: FirstConsultationSharingCardProps) {
  const { t } = useTranslation('consultations');
  const shareMutation = useShareFirstConsultationMutation();
  const unshareMutation = useUnshareFirstConsultationMutation();
  const downloadPdfMutation = useDownloadFirstConsultationPdf();
  const [isShareConfirmOpen, setIsShareConfirmOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const sharedAt = consultation.sharedWithClientAt;
  const isShared = sharedAt !== null;
  const isSharingBusy = shareMutation.isPending || unshareMutation.isPending;
  const areOwnerActionsDisabled = hasUnsavedChanges || isEditorBusy || isSharingBusy;
  const hintText = hasUnsavedChanges
    ? t('sharing.saveFirstHint')
    : isShared
      ? t('sharing.sharedHint')
      : null;
  const sharingVariables = {
    consultationId: consultation.id,
    patientId: consultation.patientId,
  };

  function confirmShare() {
    shareMutation.mutate(sharingVariables, {
      onSuccess: function notifyShared() {
        setIsShareConfirmOpen(false);
        showSuccessNotification(t('sharing.shareSuccess'));
      },
      onError: function notifyShareFailure() {
        setIsShareConfirmOpen(false);
        showErrorNotification(t('sharing.shareError'));
      },
    });
  }

  function unshare() {
    unshareMutation.mutate(sharingVariables, {
      onSuccess: function notifyUnshared() {
        showSuccessNotification(t('sharing.unshareSuccess'));
      },
      onError: function notifyUnshareFailure() {
        showErrorNotification(t('sharing.unshareError'));
      },
    });
  }

  function downloadPdf() {
    downloadPdfMutation.mutate(consultation.id, {
      onError: function notifyDownloadFailure() {
        showErrorNotification(t('sharing.downloadError'));
      },
    });
  }

  return (
    <Card>
      <p className="text-sm font-bold text-zooindigo">{t('sharing.title')}</p>
      <p className="flex items-center gap-1.5 text-xs text-zoomuted mt-2">
        <Icon name={isShared ? 'CheckCircle' : 'EyeOff'} />
        {sharedAt === null
          ? t('sharing.statusNotShared')
          : t('sharing.statusShared', { date: formatIsoDateTimeToTimestamp(sharedAt) })}
      </p>
      {hintText === null ? null : <p className="text-xs text-zoomuted mt-1">{hintText}</p>}
      <div className="flex flex-wrap gap-2 mt-3">
        <Button
          variant="outline"
          size="small"
          disabled={areOwnerActionsDisabled}
          onClick={function openPreview() {
            setIsPreviewOpen(true);
          }}
        >
          <Icon name="Eye" />
          {t('sharing.preview')}
        </Button>
        {isShared ? (
          <Button
            variant="outline"
            size="small"
            disabled={isEditorBusy || isSharingBusy}
            isLoading={unshareMutation.isPending}
            onClick={unshare}
          >
            {unshareMutation.isPending ? null : <Icon name="Undo2" />}
            {t('sharing.unshare')}
          </Button>
        ) : (
          <Button
            size="small"
            disabled={areOwnerActionsDisabled}
            onClick={function openShareConfirm() {
              setIsShareConfirmOpen(true);
            }}
          >
            <Icon name="Send" />
            {t('sharing.share')}
          </Button>
        )}
        <Button
          variant="outline"
          size="small"
          disabled={downloadPdfMutation.isPending}
          isLoading={downloadPdfMutation.isPending}
          onClick={downloadPdf}
        >
          {downloadPdfMutation.isPending ? null : <Icon name="Download" />}
          {t('sharing.downloadPdf')}
        </Button>
      </div>
      <div className="border-t border-zooborder mt-4 pt-3">
        <ConsultationVersionsList consultationId={consultation.id} />
      </div>

      {isShareConfirmOpen ? (
        <ConfirmDialog
          title={t('sharing.confirmTitle')}
          description={t('sharing.confirmMessage')}
          confirmLabel={t('sharing.confirmAction')}
          iconName="Send"
          isConfirming={shareMutation.isPending}
          onConfirm={confirmShare}
          onCancel={function cancelShare() {
            setIsShareConfirmOpen(false);
          }}
        />
      ) : null}

      {isPreviewOpen ? (
        <OwnerPreviewModal
          content={buildOwnerPreviewContent(consultation, outcomeOptions, {
            yes: t('ownerView.answerYes'),
            no: t('ownerView.answerNo'),
          })}
          onClose={function closePreview() {
            setIsPreviewOpen(false);
          }}
        />
      ) : null}
    </Card>
  );
}
